'use client';

/**
 * AuthHealthContext
 *
 * Provides centralized auth health state for call pages.
 * Wraps call pages to enable auth health monitoring and error reporting
 * from any nested component.
 *
 * Usage:
 *   // Wrap call pages
 *   <AuthHealthProvider isActiveCall={true}>
 *     <ConferencePage />
 *   </AuthHealthProvider>
 *
 *   // Use in any nested component
 *   const { isHealthy, reportAuthError, attemptReauth } = useAuthHealthContext();
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useAuthHealth, type UseAuthHealthReturn, type UseAuthHealthOptions } from '@/hooks/useAuthHealth';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

export type AuthHealthContextType = UseAuthHealthReturn;

// ============================================================================
// CONTEXT
// ============================================================================

const AuthHealthContext = createContext<AuthHealthContextType | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

export interface AuthHealthProviderProps extends UseAuthHealthOptions {
  children: React.ReactNode;
}

/**
 * Provider component for auth health context.
 * Wrap call pages with this to enable auth health monitoring.
 *
 * @example
 * ```tsx
 * <AuthHealthProvider isActiveCall={true}>
 *   <Conference />
 * </AuthHealthProvider>
 * ```
 */
export function AuthHealthProvider({
  children,
  enabled = true,
  checkInterval,
  isActiveCall = true,
}: AuthHealthProviderProps) {
  const authHealth = useAuthHealth({
    enabled,
    checkInterval,
    isActiveCall,
  });

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => authHealth, [authHealth]);

  return (
    <AuthHealthContext.Provider value={contextValue}>
      {children}
    </AuthHealthContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access auth health context.
 * Must be used within an AuthHealthProvider.
 *
 * @throws Error if used outside of AuthHealthProvider
 *
 * @example
 * ```tsx
 * const { isHealthy, reportAuthError } = useAuthHealthContext();
 *
 * // Report a 401 error
 * if (response.status === 401) {
 *   reportAuthError('files', 'Session expired');
 * }
 * ```
 */
export function useAuthHealthContext(): AuthHealthContextType {
  const context = useContext(AuthHealthContext);

  if (!context) {
    throw new Error(
      'useAuthHealthContext must be used within an AuthHealthProvider. ' +
      'Wrap your call page with <AuthHealthProvider>.'
    );
  }

  return context;
}

/**
 * Safe version of useAuthHealthContext that returns null when outside provider.
 * Useful for components that may or may not be in a call context.
 *
 * @example
 * ```tsx
 * const authHealth = useAuthHealthContextSafe();
 *
 * if (authHealth && !authHealth.isHealthy) {
 *   // Show auth warning
 * }
 * ```
 */
export function useAuthHealthContextSafe(): AuthHealthContextType | null {
  return useContext(AuthHealthContext);
}

/**
 * Hook to report auth errors from any component.
 * Returns a no-op function if outside AuthHealthProvider.
 *
 * @example
 * ```tsx
 * const reportAuthError = useReportAuthError();
 *
 * // In your API call error handling
 * if (response.status === 401) {
 *   reportAuthError('api', 'Session expired');
 * }
 * ```
 */
export function useReportAuthError(): (source: string, error?: string) => void {
  const context = useContext(AuthHealthContext);

  return useMemo(() => {
    if (!context) {
      // Return no-op if outside provider
      return () => {};
    }
    return context.reportAuthError;
  }, [context]);
}

// ============================================================================
// EXPORTS
// ============================================================================

export { AuthHealthContext };
export type { UseAuthHealthReturn, UseAuthHealthOptions };
